import loggerHelper from '../../loggerHelper';

let ctx = {};
let callbacks = {};
let ApplePaySession;
let applePayButtonSelector = null;
let applePayButtonContainer = null;
const applePayButtonTagName = 'apple-pay-button';

const init = ({
  sendMessageToMainIframe,
  sendMessageToAllFields,
  createIframes,
  globalUserOptions,
  globalCallbacks,
}) => {
  const {
    selectorForApplePayButton,
    applePayAmount,
    applePayCurrency,
    applePayCountryCode,
    applePayLabel,
    applePayButtonStyle,
    applePayButtonType,
    applePayButtonLocale,
    populateBillingFieldsWithApplePay,
  } = globalUserOptions;

  ctx = {
    sendMessageToMainIframe,
    sendMessageToAllFields,
    createIframes,
    globalUserOptions: {
      selectorForApplePayButton,
      applePayAmount,
      applePayCurrency,
      applePayCountryCode,
      applePayLabel,
      applePayButtonStyle,
      applePayButtonType,
      applePayButtonLocale,
      populateBillingFieldsWithApplePay,
    }
  };

  callbacks = globalCallbacks;
};

const createApplePaySession = () => {
  ctx.sendMessageToMainIframe({ action: 'CREATE_APPLE_PAY_SESSION' });
}

const applePayJSLoadedPromise = (url) => new Promise((resolve, reject) => {
  const element = document.createElement('script');
  element.addEventListener('load', resolve, { once: true });
  element.addEventListener('error', reject);
  element.src = url;
  element.crossOrigin = 'anonymous';
  document.head.appendChild(element);
});

const applePayButtonEl = () => document.querySelector(applePayButtonSelector).querySelector(applePayButtonTagName);

const hideApplePayButton = () => {
  applePayButtonEl().style.display = 'none';
};

const enableApplePayButton = () => {
  const el = applePayButtonEl();
  el.style.pointerEvents = 'auto';
  el.style.opacity = '1';
}

const createApplePayButton = () => {
  const {
    selectorForApplePayButton,
    applePayButtonStyle,
    applePayButtonType,
    applePayButtonLocale
  } = ctx.globalUserOptions;
  applePayButtonSelector = selectorForApplePayButton;

  applePayButtonContainer = document.querySelector(applePayButtonSelector);
  const applePayButton = document.createElement(applePayButtonTagName);
  applePayButton.id = 'cfy-apple-pay-button';
  applePayButton.setAttribute('buttonstyle', applePayButtonStyle || 'black');
  applePayButton.setAttribute('type', applePayButtonType || 'pay');
  applePayButton.setAttribute('locale', applePayButtonLocale || 'en-US');
  applePayButton.style.pointerEvents = 'none';
  applePayButton.style.opacity = '0.57';

  applePayButtonContainer.appendChild(applePayButton);
};

const initApplePay = (data) => {
  if (ApplePaySession) {
    ApplePaySession.applePayCapabilities(data.merchantIdentifier).then((capabilities) => {
      if (capabilities.paymentCredentialStatus === 'applePayUnsupported') {
        callbacks.onApplePayNotSupported();
        loggerHelper.warn('This device is not capable of making Apple Pay payments.');
        return;
      } else {
        applePayButtonEl().addEventListener('click', (e) => handleClickApplePayButton(e, data));
        enableApplePayButton();
      }
    });
  }
};

const handleClickApplePayButton = (e, data) => {
  e.preventDefault();

  const { applePayCountryCode, applePayAmount, applePayCurrency, applePayLabel } = ctx.globalUserOptions;
  const request = {
    countryCode: applePayCountryCode,
    currencyCode: applePayCurrency,
    supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
    merchantCapabilities: ['supports3DS'],
    total: { label: applePayLabel, amount: applePayAmount }
  }
  if (ctx.globalUserOptions.populateBillingFieldsWithApplePay) {
    request.requiredBillingContactFields = ['postalAddress'];
    request.requiredShippingContactFields = ['phone', 'email'];
  }
  const session = new ApplePaySession(14, request);
  session.onvalidatemerchant = () => {
    try {
      session.completeMerchantValidation(data);
    } catch (error) {
      callbacks.onApplePayError(error);
      loggerHelper.error('Error validating merchant:', error);
      session.abort();
    }
  }
  session.onpaymentauthorized = (event) => {
    try {
      const token = btoa(JSON.stringify(event.payment.token.paymentData));
      const cardDetails = fetchCardDetails(event.payment.token.paymentMethod);
      session.completePayment(ApplePaySession.STATUS_SUCCESS);
      applePayTokenReceived(token);
      callbacks.onCardDetailsChange(cardDetails);
      populateBillingInformation(
        event.payment?.billingContact,
        event.payment?.shippingContact
      );
      hideApplePayButton();
    } catch (error) {
      callbacks.onApplePayError(error);
      loggerHelper.error('Error tokenizing Apple Pay:', error);
      session.completePayment(ApplePaySession.STATUS_FAILURE);
    }
  };
  session.begin();
};

const applePaySessionCreated = async (data) => {
  await ctx.createIframes();

  createApplePayButton();

  applePayJSLoadedPromise(data.apple_pay_sdk_src).then(() => {
    if (window.ApplePaySession) {
      ApplePaySession = window.ApplePaySession;
    }
    initApplePay(data.session_data);
  }).catch((error) => loggerHelper.error('Failed to load Apple Pay JS SDK', error));
};

const applePayTokenReceived = (paymentMethodNonce) => {
  ctx.sendMessageToMainIframe({ action: 'APPLE_PAY_TOKEN_RECEIVED', data: paymentMethodNonce });
};

const populateBillingInformation = (billingContact, shippingContact) => {
  if (!ctx.globalUserOptions.populateBillingFieldsWithApplePay) return;
  if (!billingContact && !shippingContact) return;

  const addressLines = billingContact?.addressLines || [];
  const data = {
    firstName: billingContact?.givenName,
    lastName: billingContact?.familyName,
    address: addressLines[0] || null,
    address2: addressLines[1] || null,
    city: billingContact?.locality,
    state: billingContact?.administrativeArea,
    zip: billingContact?.postalCode,
    country: billingContact?.countryCode,
    email: shippingContact?.emailAddress,
    phone: shippingContact?.phoneNumber
  };

  ctx.sendMessageToAllFields({ action: 'POPULATE_BILLING_FIELDS', data });
  ctx.sendMessageToMainIframe({ action: 'APPLE_PAY_BILLING_INFO_RECEIVED', data });
}

const fetchCardDetails = (paymentMethod) => {
  if (!paymentMethod) return;
  const { type, network } = paymentMethod;

  return {
    funding_source: type,
    brands: [network?.toLowerCase()],
  }
}

export default {
  init,
  createApplePaySession,
  applePaySessionCreated,
};
